import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoadingIndicator,
  ModalVideoListVideo,
  TableContextProvider,
  Search,
  TablePaginationNew,
} from 'lib/components';
import { Button, Tooltip } from 'react-covideo-common';
import { BsPlayFill } from 'react-icons/bs';
import { Gap } from '../../styles/layout';
import { ParagraphSmallBold } from '../../styles/typography';
import { HoverPopup } from 'lib/components';
import styled, { css, useTheme } from 'styled-components/macro';
import {
  ILesaItem,
  LesaSortKey,
  useLesaTvVideosQuery,
} from 'lib/api/lesa/useLesaTvQuery';
import { useLesaVideosCopyMutation } from 'lib/api/lesa/useLesaVideosMutations';
import { getDisplayName } from 'lib/utils/functions';
import imagePlaceholder from 'assets/images/single/imagePlaceholder.webp';
import { VideoItemThumbnail } from 'app/pages/library/videos/components';
import { debounce } from 'lodash';
import { useLesaFiltersQuery } from 'lib/api/lesa/useLesaFiltersQuery';
import { MultiselectDropdown } from 'lib/components/dropdown/MultiselectDropdown';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import {
  BaseHeaderText,
  BaseModalText,
  EmptySearch,
  FullScreenModalContent,
  FullScreenModalFooter,
  PreviewModelHeader,
  VehicleMake,
  VehicleMakeHeader,
  VehicleModel,
  VehicleModelHeader,
  VehicleYear,
  VehicleYearHeader,
  VideoRow,
  VideosList,
} from '../style';

// vehicle stock
const VehicleStockNumberWidth = css`
  min-width: 120px;
  max-width: 120px;
`;
const VehicleStockNumber = styled(BaseModalText)`
  ${VehicleStockNumberWidth}
`;
const VehicleStockNumberHeader = styled(BaseHeaderText)`
  ${VehicleStockNumberWidth}
`;

type Props = {
  showAddToCovideo?: boolean;
  handleModalClose?: () => void;
  onCopy?: () => void;
  onSelectVideo?: (lesaVideo: ILesaItem) => void;
};

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 10;

export const LesaTable = ({
  handleModalClose,
  onCopy,
  showAddToCovideo = true,
  onSelectVideo,
}: Props) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [videoSource, setVideoSource] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [filter, setFilter] = useState<{
    [key: string]: string[];
  }>({});
  //sorting
  const [sortKey, setSortKey] = useState<LesaSortKey | null>(null);
  const [order, setOrder] = useState(PaginationConstants.ASCENDING);

  const resetPage = () => {
    setPage(0);
  };

  const onSuccesCallback = () => {
    onCopy?.();
    handleModalClose?.();
  };

  //queries
  const { data, isLoading: isLoadingLesaTv } = useLesaTvVideosQuery(
    {
      start: page * DEFAULT_SIZE,
      limit: DEFAULT_SIZE,
      page,
      search: searchTerm,
      filter,
      ...(!!sortKey && !!order ? { sortKey, order } : {}),
    },
    handleModalClose
  );
  const { data: filterData, isLoading: isLoadingLesaFilters } =
    useLesaFiltersQuery();

  // mutations
  const { mutateAsync: copyLesaVideosMutation, isLoading: isCopyingVideo } =
    useLesaVideosCopyMutation({
      onSuccesCallback,
    });

  const isLoading = isLoadingLesaTv || isCopyingVideo || isLoadingLesaFilters;

  const onSearchChangeHandler = async (search: string = '') => {
    setSearchTerm(search);
    resetPage();
  };

  const onPaginationChangeHandler = debounce(
    async ({ page: newPage }: { page: number }) => {
      if (page !== newPage) {
        setPage(newPage);
      }
    },
    300
  );

  const copyLesaMutationHandler = (video: ILesaItem) => {
    if (!video.video) {
      return;
    }

    const title = getDisplayName([
      video?.year.toString(),
      video.make,
      video.model,
      video.stockNumber,
    ]);

    copyLesaVideosMutation({
      url: video.video?.url,
      fileName: title,
      lesaVehicleId: video.lesaVehicleId,
    });
  };

  const showSelectVideo = !!onSelectVideo;
  const theme = useTheme();

  const handleFilterChange = (key: string, selectedOption: string[]) => {
    if (key === 'make') {
      setFilter(prevState => ({
        ...prevState,
        [key]: selectedOption,
        model: [],
      }));
      return;
    }
    setFilter(prevState => ({
      ...prevState,
      [key]: selectedOption,
    }));
  };

  const handleSortChange = (
    sortKey: LesaSortKey,
    order: PaginationConstants
  ) => {
    setOrder(order);
    setSortKey(sortKey);
    setPage(DEFAULT_PAGE);
  };

  const getEmptyStateMessage = () => {
    const hasFilters = Object.values(filter).some(
      val => Array.isArray(val) && val.length > 0
    );

    if (searchTerm && hasFilters) {
      return {
        primary: t(
          'No videos found matching "{{searchTerm}}" with your selected filters.',
          { searchTerm }
        ),
        secondary: t(
          'Try adjusting your filters or using a different search term.'
        ),
      };
    }
    if (searchTerm) {
      return {
        primary: t('There are no videos with "{{searchTerm}}" in the title.', {
          searchTerm,
        }),
        secondary: t('Try using a different search term.'),
      };
    }
    if (hasFilters) {
      return {
        primary: t('No videos match your selected filters.'),
        secondary: t('Try selecting different filter options.'),
      };
    }
    return {
      primary: t(
        "Unfortunately, there aren't any videos available at the moment."
      ),
      secondary: '',
    };
  };

  return (
    <FullScreenModalContent>
      <Gap
        m='0 0 20px auto'
        alignItems='flex-end'
        justifyContent='space-between'
        width='100%'
      >
        {filterData && (
          <Gap gap='12px'>
            {filterData.filters?.map(item => {
              // custom rule for model dropdown
              if (item.key === 'model') {
                const isDisabled =
                  isLoadingLesaFilters || !filter?.make?.length;
                return (
                  <Tooltip
                    popup={isDisabled ? t('Select Make first') : ''}
                    position='bottom'
                  >
                    <MultiselectDropdown
                      key={`model-${filter.make?.join('-')}`}
                      onOptionsSelected={value =>
                        handleFilterChange(item.key, value as string[])
                      }
                      options={item.options.filter(
                        option =>
                          filter.make &&
                          option.group &&
                          filter.make.includes(option.group)
                      )}
                      menuListValue={-1}
                      menuListLabel={t('All {{name}}s', { name: item.name })}
                      name={`${item.name}`}
                      menuShouldBlockScroll={false}
                      isLoading={isLoadingLesaFilters}
                      selectInputWidth='200px'
                      menuWidth='280px'
                      isDisabled={isDisabled}
                      isSearchable
                      isClearable
                    />
                  </Tooltip>
                );
              }
              return (
                <MultiselectDropdown
                  key={item.key}
                  onOptionsSelected={value =>
                    handleFilterChange(item.key, value as string[])
                  }
                  options={item.options}
                  menuListValue={-1}
                  menuListLabel={t('All {{name}}s', { name: item.name })}
                  name={`${item.name}`}
                  menuShouldBlockScroll={false}
                  isLoading={isLoadingLesaFilters}
                  selectInputWidth='200px'
                  menuWidth='280px'
                  isDisabled={isLoadingLesaFilters}
                  isSearchable
                  isClearable
                />
              );
            })}
          </Gap>
        )}
        <Search
          placeholder={t('Search videos...')}
          onSearch={onSearchChangeHandler}
          width='300px'
          disabled={isCopyingVideo}
        />
      </Gap>
      {isLoading ? (
        <div>
          <LoadingIndicator isLoading={isLoading} />
        </div>
      ) : (
        <>
          {!data?.videos?.length ? (
            <EmptySearch>
              {(() => {
                const message = getEmptyStateMessage();
                return (
                  <>
                    <ParagraphSmallBold color={theme.colors.secondary[60]}>
                      {message.primary}
                    </ParagraphSmallBold>
                    {message.secondary && (
                      <ParagraphSmallBold color={theme.colors.secondary[60]}>
                        {message.secondary}
                      </ParagraphSmallBold>
                    )}
                  </>
                );
              })()}
            </EmptySearch>
          ) : (
            <>
              <VideosList>
                <VideoRow>
                  <PreviewModelHeader>{t('Preview')}</PreviewModelHeader>
                  <VehicleStockNumberHeader>
                    {t('Stock #')}
                    <SortButtons
                      isSorted={sortKey === LesaSortKey.STOCK_NUMBER}
                      order={order}
                      onChange={value =>
                        handleSortChange(LesaSortKey.STOCK_NUMBER, value)
                      }
                    />
                  </VehicleStockNumberHeader>
                  <VehicleYearHeader>
                    {t('Year')}
                    <SortButtons
                      isSorted={sortKey === LesaSortKey.YEAR}
                      order={order}
                      onChange={value =>
                        handleSortChange(LesaSortKey.YEAR, value)
                      }
                    />
                  </VehicleYearHeader>
                  <VehicleMakeHeader>
                    {t('Make')}
                    <SortButtons
                      isSorted={sortKey === LesaSortKey.MAKE}
                      order={order}
                      onChange={value =>
                        handleSortChange(LesaSortKey.MAKE, value)
                      }
                    />
                  </VehicleMakeHeader>
                  <VehicleModelHeader>
                    {t('Model')}
                    <SortButtons
                      isSorted={sortKey === LesaSortKey.MODEL}
                      order={order}
                      onChange={value =>
                        handleSortChange(LesaSortKey.MODEL, value)
                      }
                    />
                  </VehicleModelHeader>
                </VideoRow>
                {data?.videos?.map(video => (
                  <VideoRow key={video.postingId}>
                    <VideoItemThumbnail
                      handleClick={() => {
                        if (!!video.video?.url) {
                          setVideoSource(video.video?.url);
                        }
                      }}
                      thumbnail={video.thumbnailUrl || imagePlaceholder}
                      videoId={video?.video?.uuid || ''}
                      processing={0}
                      thumbnailHeight={'80%'}
                      thumbnailWidth={'15%'}
                    />
                    <VehicleStockNumber>{video.stockNumber}</VehicleStockNumber>
                    <VehicleYear>{video.year}</VehicleYear>
                    <VehicleMake>{video.make}</VehicleMake>
                    <VehicleModel>{video.model}</VehicleModel>
                    <Gap flexWrap='noWrap' gap='10px'>
                      {video.video === null ? (
                        <>
                          <HoverPopup
                            width='170px'
                            position='above'
                            padding='8px 12px'
                            hoverable={
                              <Button
                                disabled={true}
                                variant='secondary'
                                icon={<BsPlayFill />}
                                onClick={() => null}
                              />
                            }
                            popup={<>{t('Preview not available!')}</>}
                          />
                          <HoverPopup
                            width='155px'
                            position='above'
                            padding='8px 12px'
                            hoverable={
                              <Button
                                disabled={true}
                                variant='secondary'
                                text='Add to Covideo'
                                onClick={() => null}
                              />
                            }
                            popup={<>{t('Video not available!')}</>}
                          />
                        </>
                      ) : (
                        <>
                          {showAddToCovideo && (
                            <Button
                              variant='secondary'
                              text={t('Add to Covideo')}
                              onClick={() => copyLesaMutationHandler(video)}
                            />
                          )}
                          {showSelectVideo && (
                            <Button
                              variant='secondary'
                              text={t('Select Video')}
                              onClick={() => onSelectVideo(video)}
                            />
                          )}
                        </>
                      )}
                    </Gap>
                  </VideoRow>
                ))}
              </VideosList>
              <FullScreenModalFooter>
                <TableContextProvider
                  total={data?.count || 0}
                  initSize={DEFAULT_SIZE}
                  onChange={onPaginationChangeHandler}
                  initPage={page}
                >
                  <TablePaginationNew disabled={isLoading} />
                </TableContextProvider>
              </FullScreenModalFooter>
            </>
          )}

          {!!videoSource && (
            <ModalVideoListVideo
              videoId={''}
              source={videoSource}
              handleModalClose={() => {
                setVideoSource('');
              }}
              hideContext={true}
            />
          )}
        </>
      )}
    </FullScreenModalContent>
  );
};
